export interface AuthConfig {
  authority: string;
  clientId: string;
  audience: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  /**
   * Authentication mode.
   * - stub: generates unsigned JWTs (local/dev)
   * - oidc: uses OIDC Authorization Code Flow with PKCE (demo/prod-like)
   */
  mode?: "stub" | "oidc";
  tenantId?: string;
  organizationId?: string;
  /**
   * Optional default user profile used by stubs and local development to
   * represent the authenticated principal returned from SIS. This is helpful
   * when a UI wants to render role-based experiences without a real identity
   * provider being present.
   */
  defaultUser?: Partial<AuthUser>;
  defaultScopes?: string[];
  /**
   * Where authenticated state is retained in a browser. The default preserves
   * the existing application behaviour; memory mode keeps tokens only in this
   * AuthClient instance while retaining the short-lived PKCE transaction in
   * session storage.
   */
  tokenStorage?: "localStorage" | "memory";
}

export { createDefaultAuthConfig, resolveAuthOrigin, type AuthConfigDefaults } from "./authConfig";
export { buildAuthHeadersForClient, type AuthHeaderDefaults } from "./headers";
export {
  createAuthFetch,
  createAuthHeaderMatcher,
  installAuthFetchInterceptor,
  type AuthFetchOptions,
  type AuthHeaderMatcherOptions,
} from "./fetch";
export {
  HEADER_TENANT_ID,
  HEADER_ORGANIZATION_ID,
  HEADER_COMPANY_ID,
  HEADER_EMPLOYEE_ID,
  HEADER_AUTHORIZATION,
  HEADER_CORRELATION_ID,
} from "./headerNames";
export { generateCorrelationId } from "./correlation";
export { buildRequestContextHeaders, type RequestContext } from "./requestContext";

export interface AuthUser {
  id: string;
  /** Tenant-bound Parties identity used for audit-bearing writes. */
  partId?: string;
  customerId?: string;
  email?: string;
  name?: string;
  tenantId?: string;
  organizationId?: string;
  roles?: string[];
  scopes?: string[];
}

export interface AuthTokens {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt?: number;
}

export interface AuthState {
  isAuthenticated: boolean;
  user?: AuthUser;
  tokens: AuthTokens;
  tenantId?: string;
}

export type AuthStateSubscriber = (state: AuthState) => void;

const defaultState: AuthState = {
  isAuthenticated: false,
  tokens: {},
};

type OidcDiscovery = {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
  issuer?: string;
};

type OidcTokenResponse = {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
};

const STORAGE_PREFIX = "stenpro.auth";
const STORAGE_TOKENS = `${STORAGE_PREFIX}.tokens`;
const STORAGE_USER = `${STORAGE_PREFIX}.user`;
const STORAGE_TENANT = `${STORAGE_PREFIX}.tenant`;
const STORAGE_OIDC_STATE = `${STORAGE_PREFIX}.oidc.state`;
const STORAGE_OIDC_VERIFIER = `${STORAGE_PREFIX}.oidc.verifier`;
const STORAGE_OIDC_RETURN_TO = `${STORAGE_PREFIX}.oidc.return_to`;

function base64UrlEncode(value: string): string {
  if (typeof window === "undefined") {
    return Buffer.from(value).toString("base64url");
  }

  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function generateFakeToken(payload: Record<string, unknown>): string {
  const issuedAt = Math.floor(Date.now() / 1000);
  const resolvedPayload = {
    iat: issuedAt,
    exp: issuedAt + 3600,
    ...payload,
  };

  const header = base64UrlEncode(JSON.stringify({ alg: "none", typ: "JWT" }));
  const body = base64UrlEncode(JSON.stringify(resolvedPayload));
  return `${header}.${body}.`;
}

function toUrlEncodedBody(values: Record<string, string>): string {
  return Object.entries(values)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
}

function safeJsonParse<T>(value: string | null): T | undefined {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

function decodeJwtPayload(token?: string): any {
  if (!token) {
    return undefined;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return undefined;
  }

  try {
    const decoded = base64UrlDecode(parts[1]);
    return JSON.parse(decoded);
  } catch {
    return undefined;
  }
}

function resolveClaimString(payload: any, ...keys: string[]): string | undefined {
  if (!payload) {
    return undefined;
  }

  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
}

function collectTokenRoles(payload: any): string[] {
  if (!payload) {
    return [];
  }

  const roles = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === "string" && value.trim().length > 0) {
      roles.add(value.trim());
    }
  };
  const addList = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }

    add(value);
  };

  addList(payload.roles);
  addList(payload.role);

  const realmAccess = payload.realm_access;
  if (realmAccess && typeof realmAccess === "object") {
    addList(realmAccess.roles);
  }

  const resourceAccess = payload.resource_access;
  if (resourceAccess && typeof resourceAccess === "object") {
    for (const clientAccess of Object.values(resourceAccess)) {
      if (clientAccess && typeof clientAccess === "object") {
        addList((clientAccess as { roles?: unknown }).roles);
      }
    }
  }

  return [...roles].sort((left, right) => left.localeCompare(right));
}

function resolveTokenUserId(accessPayload: any, idPayload: any, fallback = "user"): string {
  return (
    resolveClaimString(accessPayload, "project_user_id", "projectUserId") ??
    resolveClaimString(idPayload, "project_user_id", "projectUserId") ??
    resolveClaimString(
      accessPayload,
      "nameidentifier",
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier",
    ) ??
    resolveClaimString(idPayload, "sub") ??
    resolveClaimString(accessPayload, "sub") ??
    fallback
  );
}

export function resolveTokenPartId(accessPayload: any, idPayload: any): string | undefined {
  return (
    resolveClaimString(accessPayload, "part_id", "partId", "actor_id", "actorId") ??
    resolveClaimString(idPayload, "part_id", "partId", "actor_id", "actorId")
  );
}

function randomString(byteLength = 32): string {
  if (typeof window === "undefined") {
    const bytes = require("crypto").randomBytes(byteLength) as Buffer;
    return bytes.toString("base64url");
  }

  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function utf8Encode(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function bytesToBinary(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return binary;
}

function sha256Fallback(data: Uint8Array): Uint8Array {
  const rightRotate = (value: number, amount: number): number => (value >>> amount) | (value << (32 - amount));
  const words: number[] = [];
  for (let i = 0; i < data.length; i += 1) {
    words[i >> 2] = (words[i >> 2] ?? 0) | (data[i] << (24 - (i % 4) * 8));
  }

  const bitLength = data.length * 8;
  words[bitLength >> 5] = (words[bitLength >> 5] ?? 0) | (0x80 << (24 - (bitLength % 32)));
  words[(((bitLength + 64) >> 9) << 4) + 15] = bitLength;

  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Array<number>(64);
  for (let i = 0; i < words.length; i += 16) {
    for (let t = 0; t < 16; t += 1) {
      w[t] = words[i + t] ?? 0;
    }
    for (let t = 16; t < 64; t += 1) {
      const s0 = rightRotate(w[t - 15], 7) ^ rightRotate(w[t - 15], 18) ^ (w[t - 15] >>> 3);
      const s1 = rightRotate(w[t - 2], 17) ^ rightRotate(w[t - 2], 19) ^ (w[t - 2] >>> 10);
      w[t] = (((w[t - 16] + s0) | 0) + ((w[t - 7] + s1) | 0)) | 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let t = 0; t < 64; t += 1) {
      const s1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = ((((h + s1) | 0) + ((ch + k[t]) | 0)) + w[t]) | 0;
      const s0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) | 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) | 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) | 0;
    }

    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  const hashWords = [h0, h1, h2, h3, h4, h5, h6, h7];
  const output = new Uint8Array(32);
  for (let i = 0; i < hashWords.length; i += 1) {
    output[i * 4] = (hashWords[i] >>> 24) & 0xff;
    output[i * 4 + 1] = (hashWords[i] >>> 16) & 0xff;
    output[i * 4 + 2] = (hashWords[i] >>> 8) & 0xff;
    output[i * 4 + 3] = hashWords[i] & 0xff;
  }
  return output;
}

async function sha256Base64Url(value: string): Promise<string> {
  if (typeof window === "undefined") {
    const crypto = require("crypto") as typeof import("crypto");
    return crypto.createHash("sha256").update(value).digest("base64url");
  }

  const subtle = globalThis.crypto?.subtle;
  const data = utf8Encode(value);
  if (subtle) {
    const digestInput = new Uint8Array(data);
    const digest = await subtle.digest("SHA-256", digestInput.buffer);
    return btoa(bytesToBinary(new Uint8Array(digest))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  return btoa(bytesToBinary(sha256Fallback(data))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(value: string): string {
  if (typeof window === "undefined") {
    return Buffer.from(value, "base64url").toString("utf8");
  }

  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function readTokenExpiration(token?: string): number | undefined {
  if (!token) {
    return undefined;
  }

  const [, payload] = token.split(".");
  if (!payload) {
    return undefined;
  }

  try {
    const decoded = base64UrlDecode(payload);
    const parsed = JSON.parse(decoded);
    return typeof parsed.exp === "number" ? parsed.exp : undefined;
  } catch {
    return undefined;
  }
}

export class AuthClient {
  private state: AuthState = { ...defaultState };
  private subscribers: AuthStateSubscriber[] = [];
  private readonly config: AuthConfig;
  private discovery?: OidcDiscovery;
  private pendingOidcCallback?: Promise<void>;

  constructor(config: AuthConfig) {
    this.config = config;
    this.hydrateFromStorage();
  }

  getState(): AuthState {
    return this.state;
  }

  onStateChange(subscriber: AuthStateSubscriber): () => void {
    this.subscribers.push(subscriber);
    subscriber(this.state);

    return () => {
      this.subscribers = this.subscribers.filter((candidate) => candidate !== subscriber);
    };
  }

  async loginWithRedirect(options?: {
    tenantId?: string;
    customerId?: string;
    email?: string;
    name?: string;
    roles?: string[];
    id?: string;
    scopes?: string[];
    organizationId?: string;
  }): Promise<AuthState> {
    if (this.resolveMode() === "oidc") {
      return this.loginWithOidcRedirect();
    }

    const tenantId = options?.tenantId ?? this.config.tenantId;
    const organizationId = options?.organizationId ?? this.config.organizationId ?? this.config.defaultUser?.organizationId;
    const customerId = options?.customerId ?? this.config.defaultUser?.customerId;
    const resolvedRoles = options?.roles ?? this.config.defaultUser?.roles ?? [];
    const resolvedScopes = options?.scopes ?? this.config.defaultUser?.scopes ?? this.config.defaultScopes ?? ["openid", "profile"];
    const resolvedUser: AuthUser = {
      id: options?.id ?? options?.email ?? this.config.defaultUser?.id ?? this.config.defaultUser?.email ?? "user",
      customerId,
      email: options?.email ?? this.config.defaultUser?.email,
      name: options?.name ?? this.config.defaultUser?.name ?? "StenPro User",
      tenantId,
      organizationId,
      roles: resolvedRoles,
      scopes: resolvedScopes,
    };

    const scopeValue = resolvedScopes.length > 0 ? resolvedScopes.join(" ") : undefined;
    const baseClaims = {
      sub: resolvedUser.id,
      user_id: resolvedUser.id,
      aud: this.config.audience,
      iss: this.config.authority,
      tenant_id: tenantId,
      organization_id: organizationId,
      customer_id: customerId,
      roles: resolvedRoles,
      scopes: resolvedScopes,
      scope: scopeValue,
      scp: scopeValue,
    };

    const accessToken = generateFakeToken({
      ...baseClaims,
      token_use: "access",
    });
    const idToken = generateFakeToken({
      ...baseClaims,
      aud: this.config.clientId,
      email: resolvedUser.email,
      name: resolvedUser.name,
      token_use: "id",
    });
    const refreshToken = generateFakeToken({
      ...baseClaims,
      aud: `${this.config.audience}:refresh`,
      token_use: "refresh",
      exp: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 7,
    });

    const newState: AuthState = {
      isAuthenticated: true,
      tenantId,
      tokens: {
        accessToken,
        idToken,
        refreshToken,
        expiresAt: this.epochSeconds() + 3600,
      },
      user: resolvedUser,
    };

    this.setState(newState);
    return newState;
  }

  async logout(): Promise<void> {
    if (this.resolveMode() === "oidc") {
      const discovery = await this.getDiscovery().catch(() => undefined);
      this.clearStorage();
      this.setState({ ...defaultState });

      if (typeof window !== "undefined" && discovery?.end_session_endpoint) {
        const url = new URL(discovery.end_session_endpoint);
        url.searchParams.set("client_id", this.config.clientId);
        url.searchParams.set("post_logout_redirect_uri", this.config.postLogoutRedirectUri);
        window.location.assign(url.toString());
      }

      return;
    }

    this.clearStorage();
    this.setState({ ...defaultState });
  }

  async refreshTokens(options?: {
    tenantId?: string;
    customerId?: string;
    email?: string;
    name?: string;
    roles?: string[];
    id?: string;
    scopes?: string[];
    organizationId?: string;
  }): Promise<AuthState> {
    if (this.resolveMode() === "oidc") {
      return this.refreshOidcTokens();
    }

    if (!this.state.isAuthenticated) {
      return this.loginWithRedirect({
        tenantId: options?.tenantId ?? this.config.tenantId,
        customerId: options?.customerId ?? this.config.defaultUser?.customerId,
        email: options?.email ?? this.config.defaultUser?.email,
        name: options?.name ?? this.config.defaultUser?.name,
        roles: options?.roles ?? this.config.defaultUser?.roles,
        id: options?.id ?? this.config.defaultUser?.id,
        organizationId: options?.organizationId ?? this.config.defaultUser?.organizationId ?? this.config.organizationId,
        scopes: options?.scopes ?? this.config.defaultUser?.scopes ?? this.config.defaultScopes,
      });
    }

    const tenantId = options?.tenantId ?? this.state.tenantId ?? this.config.tenantId;
    const organizationId =
      options?.organizationId ?? this.state.user?.organizationId ?? this.config.defaultUser?.organizationId ?? this.config.organizationId;
    const customerId = options?.customerId ?? this.state.user?.customerId ?? this.config.defaultUser?.customerId;
    const roles = options?.roles ?? this.state.user?.roles ?? this.config.defaultUser?.roles ?? [];
    const scopes = options?.scopes ?? this.state.user?.scopes ?? this.config.defaultUser?.scopes ?? this.config.defaultScopes ?? ["openid", "profile"];
    const scopeValue = scopes.length > 0 ? scopes.join(" ") : undefined;
    const refreshedState: AuthState = {
      ...this.state,
      tenantId,
      user: this.state.user
        ? { ...this.state.user, customerId, tenantId, organizationId, roles, scopes }
        : {
            id: options?.id ?? options?.email ?? this.config.defaultUser?.id ?? "user",
            customerId,
            tenantId,
            organizationId,
            roles,
            scopes,
          },
      tokens: {
        ...this.state.tokens,
        accessToken: generateFakeToken({
          sub: this.state.user?.id ?? options?.id ?? options?.email ?? "user",
          user_id: this.state.user?.id ?? options?.id ?? options?.email ?? "user",
          aud: this.config.audience,
          iss: this.config.authority,
          tenant_id: tenantId,
          organization_id: organizationId,
          customer_id: customerId,
          roles,
          scopes,
          scope: scopeValue,
          scp: scopeValue,
          token_use: "access",
        }),
        expiresAt: this.epochSeconds() + 3600,
      },
    };

    this.setState(refreshedState);
    return refreshedState;
  }

  async ensureValidAccessToken(options?: { bufferSeconds?: number } & Pick<AuthConfig, "tenantId" | "organizationId">): Promise<AuthState> {
    if (this.resolveMode() === "oidc") {
      if (!this.pendingOidcCallback) {
        this.pendingOidcCallback = this.handleOidcCallbackIfPresent().finally(() => {
          this.pendingOidcCallback = undefined;
        });
      }
      await this.pendingOidcCallback;

      // Do not auto-trigger the OIDC redirect flow from background refreshes or
      // generic fetch interception. An explicit login action should start the
      // redirect when no authenticated session exists yet.
      if (!this.state.isAuthenticated && !this.state.tokens.refreshToken && !this.state.tokens.accessToken) {
        return this.state;
      }

      const bufferSeconds = options?.bufferSeconds ?? 60;
      const needsRefresh =
        !this.state.isAuthenticated || this.isAccessTokenExpired(bufferSeconds) || this.isRefreshTokenExpired(bufferSeconds * 5);

      if (needsRefresh) {
        return this.refreshOidcTokens();
      }

      return this.state;
    }

    const bufferSeconds = options?.bufferSeconds ?? 60;
    const needsRefresh =
      !this.state.isAuthenticated ||
      this.isAccessTokenExpired(bufferSeconds) ||
      this.isRefreshTokenExpired(bufferSeconds * 5) ||
      this.shouldRefreshForScopes(this.config.defaultScopes);

    if (needsRefresh) {
      return this.refreshTokens({
        tenantId: options?.tenantId,
        organizationId: options?.organizationId,
        scopes: this.config.defaultScopes,
      });
    }

    return this.state;
  }

  private resolveMode(): "stub" | "oidc" {
    return this.config.mode ?? "stub";
  }

  private shouldRefreshForScopes(requiredScopes?: string[]): boolean {
    if (!requiredScopes || requiredScopes.length === 0) {
      return false;
    }

    const currentScopes = this.state.user?.scopes ?? [];
    if (currentScopes.length === 0) {
      return true;
    }

    const currentScopeSet = new Set(currentScopes.map((scope) => scope.toLowerCase()));
    return requiredScopes.some((scope) => !currentScopeSet.has(scope.toLowerCase()));
  }

  private hydrateFromStorage(): void {
    if (typeof window === "undefined" || this.usesMemoryTokenStorage()) {
      return;
    }

    const storedTokens = safeJsonParse<AuthTokens>(window.localStorage.getItem(STORAGE_TOKENS));
    const storedUser = safeJsonParse<AuthUser>(window.localStorage.getItem(STORAGE_USER));
    const storedTenant = window.localStorage.getItem(STORAGE_TENANT) ?? undefined;

    if (storedTokens?.accessToken) {
      const expiresAt = storedTokens.expiresAt ?? readTokenExpiration(storedTokens.accessToken);
      this.state = {
        isAuthenticated: true,
        tenantId: storedTenant,
        user: storedUser,
        tokens: {
          ...storedTokens,
          expiresAt,
        },
      };
    }
  }

  private persistToStorage(state: AuthState): void {
    if (typeof window === "undefined" || this.usesMemoryTokenStorage()) {
      return;
    }

    if (!state.isAuthenticated) {
      this.clearStorage();
      return;
    }

    window.localStorage.setItem(STORAGE_TOKENS, JSON.stringify(state.tokens));
    window.localStorage.setItem(STORAGE_USER, JSON.stringify(state.user ?? null));
    if (state.tenantId) {
      window.localStorage.setItem(STORAGE_TENANT, state.tenantId);
    }
  }

  private clearStorage(): void {
    if (typeof window === "undefined") {
      return;
    }

    if (!this.usesMemoryTokenStorage()) {
      window.localStorage.removeItem(STORAGE_TOKENS);
      window.localStorage.removeItem(STORAGE_USER);
      window.localStorage.removeItem(STORAGE_TENANT);
    }
    window.sessionStorage.removeItem(STORAGE_OIDC_STATE);
    window.sessionStorage.removeItem(STORAGE_OIDC_VERIFIER);
    window.sessionStorage.removeItem(STORAGE_OIDC_RETURN_TO);
  }

  private async getDiscovery(): Promise<OidcDiscovery> {
    if (this.discovery) {
      return this.discovery;
    }

    const discoveryUrl = `${this.config.authority.replace(/\/+$/, "")}/.well-known/openid-configuration`;
    const response = await fetch(discoveryUrl);
    if (!response.ok) {
      throw new Error(`OIDC discovery failed (${response.status})`);
    }
    const json = (await response.json()) as OidcDiscovery;
    if (!json.authorization_endpoint || !json.token_endpoint) {
      throw new Error("OIDC discovery missing endpoints");
    }
    this.discovery = json;
    return json;
  }

  private async loginWithOidcRedirect(): Promise<AuthState> {
    if (typeof window === "undefined") {
      throw new Error("OIDC login requires a browser");
    }

    const discovery = await this.getDiscovery();
    const state = randomString(16);
    const verifier = randomString(64);
    const challengeMethod: "S256" = "S256";
    const challenge = await sha256Base64Url(verifier);

    window.sessionStorage.setItem(STORAGE_OIDC_STATE, state);
    window.sessionStorage.setItem(STORAGE_OIDC_VERIFIER, verifier);
    if (!this.usesMemoryTokenStorage()) {
      const currentReturnTo = `${window.location.pathname}${window.location.search}${window.location.hash}` || "/";
      const existingReturnTo = window.sessionStorage.getItem(STORAGE_OIDC_RETURN_TO);
      const returnTo =
        existingReturnTo && existingReturnTo.startsWith("/") && !existingReturnTo.startsWith("/login")
          ? existingReturnTo
          : currentReturnTo.startsWith("/login") || currentReturnTo.startsWith("/auth/callback")
            ? "/"
            : currentReturnTo;
      window.sessionStorage.setItem(STORAGE_OIDC_RETURN_TO, returnTo);
    } else {
      window.sessionStorage.removeItem(STORAGE_OIDC_RETURN_TO);
    }

    const scope = (this.config.defaultScopes ?? ["openid", "profile", "email"]).join(" ");

    const url = new URL(discovery.authorization_endpoint);
    url.searchParams.set("client_id", this.config.clientId);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("redirect_uri", this.config.redirectUri);
    url.searchParams.set("scope", scope);
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", challengeMethod);

    window.location.assign(url.toString());
    return this.state;
  }

  private async handleOidcCallbackIfPresent(): Promise<void> {
    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    if (!code || !state) {
      return;
    }

    const expectedState = window.sessionStorage.getItem(STORAGE_OIDC_STATE);
    const verifier = window.sessionStorage.getItem(STORAGE_OIDC_VERIFIER);
    if (!expectedState || expectedState !== state || !verifier) {
      throw new Error("Invalid OIDC state");
    }

    // Remove callback params before token exchange so repeated renders/refresh checks
    // do not retry the same authorization code.
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("session_state");
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    window.sessionStorage.removeItem(STORAGE_OIDC_STATE);
    window.sessionStorage.removeItem(STORAGE_OIDC_VERIFIER);
    const returnTo = this.usesMemoryTokenStorage()
      ? undefined
      : window.sessionStorage.getItem(STORAGE_OIDC_RETURN_TO) ?? undefined;
    if (!this.usesMemoryTokenStorage()) {
      window.sessionStorage.removeItem(STORAGE_OIDC_RETURN_TO);
    }

    const discovery = await this.getDiscovery();
    const body = toUrlEncodedBody({
      grant_type: "authorization_code",
      client_id: this.config.clientId,
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: verifier,
    });

    const response = await fetch(discovery.token_endpoint, {
      method: "POST",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`OIDC token exchange failed (${response.status})`);
    }

    const tokens = (await response.json()) as OidcTokenResponse;
    const now = this.epochSeconds();
    const expiresAt = tokens.expires_in ? now + tokens.expires_in : readTokenExpiration(tokens.access_token);
    const idPayload = decodeJwtPayload(tokens.id_token);
    const accessPayload = decodeJwtPayload(tokens.access_token);

    const tenantId =
      resolveClaimString(accessPayload, "tenant_id", "tenantId") ??
      resolveClaimString(idPayload, "tenant_id", "tenantId") ??
      this.config.tenantId;
    const organizationId =
      resolveClaimString(accessPayload, "organization_id", "organizationId") ??
      resolveClaimString(idPayload, "organization_id", "organizationId") ??
      this.config.organizationId;
    const roles = collectTokenRoles(accessPayload);
    const scopes = Array.isArray(accessPayload?.scopes)
      ? accessPayload.scopes
      : typeof accessPayload?.scope === "string"
        ? accessPayload.scope.split(" ")
        : this.config.defaultScopes;

    const newState: AuthState = {
      isAuthenticated: true,
      tenantId,
      tokens: {
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
        expiresAt,
      },
      user: {
        id: resolveTokenUserId(accessPayload, idPayload),
        partId: resolveTokenPartId(accessPayload, idPayload),
        email: idPayload?.email,
        name: idPayload?.name,
        tenantId,
        organizationId,
        roles,
        scopes,
      },
    };

    this.setState(newState);

    const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (returnTo && returnTo !== currentLocation) {
      window.location.replace(returnTo);
    }
  }

  private async refreshOidcTokens(): Promise<AuthState> {
    const refreshToken = this.state.tokens.refreshToken;
    if (!refreshToken) {
      // No refresh token -> force re-login
      if (typeof window !== "undefined") {
        return this.loginWithOidcRedirect();
      }
      return this.state;
    }

    const discovery = await this.getDiscovery();
    const body = toUrlEncodedBody({
      grant_type: "refresh_token",
      client_id: this.config.clientId,
      refresh_token: refreshToken,
    });

    const response = await fetch(discovery.token_endpoint, {
      method: "POST",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      body,
    });

    if (!response.ok) {
      // A stale app-local refresh token must not trigger a federated logout.
      // The user may still have a valid Identity session that an explicit
      // app-switch SSO launch can use.
      this.clearStorage();
      this.setState({ ...defaultState });
      return this.state;
    }

    const tokens = (await response.json()) as OidcTokenResponse;
    const now = this.epochSeconds();
    const expiresAt = tokens.expires_in ? now + tokens.expires_in : readTokenExpiration(tokens.access_token);
    const idPayload = decodeJwtPayload(tokens.id_token ?? this.state.tokens.idToken);
    const accessPayload = decodeJwtPayload(tokens.access_token ?? this.state.tokens.accessToken);
    const roles = collectTokenRoles(accessPayload);
    const scopes = Array.isArray(accessPayload?.scopes)
      ? accessPayload.scopes
      : typeof accessPayload?.scope === "string"
        ? accessPayload.scope.split(" ")
        : this.state.user?.scopes;

    const tenantId =
      resolveClaimString(accessPayload, "tenant_id", "tenantId") ??
      resolveClaimString(idPayload, "tenant_id", "tenantId") ??
      this.state.tenantId ??
      this.config.tenantId;
    const organizationId =
      resolveClaimString(accessPayload, "organization_id", "organizationId") ??
      resolveClaimString(idPayload, "organization_id", "organizationId") ??
      this.state.user?.organizationId ??
      this.config.organizationId;

    const refreshedState: AuthState = {
      ...this.state,
      isAuthenticated: true,
      tenantId,
      tokens: {
        accessToken: tokens.access_token ?? this.state.tokens.accessToken,
        idToken: tokens.id_token ?? this.state.tokens.idToken,
        refreshToken: tokens.refresh_token ?? this.state.tokens.refreshToken,
        expiresAt,
      },
      user: this.state.user
        ? {
            ...this.state.user,
            id: resolveTokenUserId(accessPayload, idPayload, this.state.user.id),
            partId: resolveTokenPartId(accessPayload, idPayload) ?? this.state.user.partId,
            email: idPayload?.email ?? this.state.user.email,
            name: idPayload?.name ?? this.state.user.name,
            tenantId,
            organizationId,
            roles: roles.length > 0 ? roles : this.state.user.roles,
            scopes: scopes ?? this.state.user.scopes,
          }
          : {
            id: resolveTokenUserId(accessPayload, idPayload),
            partId: resolveTokenPartId(accessPayload, idPayload),
            tenantId,
            organizationId,
            roles,
            scopes: scopes ?? [],
          },
    };

    this.setState(refreshedState);
    return refreshedState;
  }

  getAuthorizationHeader(): string | undefined {
    return this.state.tokens.accessToken ? `Bearer ${this.state.tokens.accessToken}` : undefined;
  }

  buildTenantHeaders(explicitTenant?: string, explicitOrganization?: string): Record<string, string> {
    // Avoid emitting malformed request-context headers like "undefined"/"null" and
    // ensure request-context values are not empty or placeholder strings.
    const normalize = (value: unknown) => {
      const trimmed = value === undefined || value === null ? "" : String(value).trim();
      if (!trimmed) {
        return undefined;
      }
      const lower = trimmed.toLowerCase();
      if (lower === "undefined" || lower === "null") {
        return undefined;
      }
      return trimmed;
    };

    const tenantCandidate = normalize(explicitTenant ?? this.state.tenantId ?? this.config.tenantId);
    const tenantId = tenantCandidate;
    const organizationId = normalize(explicitOrganization ?? this.state.user?.organizationId ?? this.config.organizationId);

    const headers: Record<string, string> = {};
    if (tenantId) {
      headers["X-Tenant-Id"] = tenantId;
    }
    if (organizationId) {
      headers["X-Organization-Id"] = organizationId;
    }

    return headers;
  }

  switchTenant(tenantId: string, organizationId?: string): Promise<AuthState> {
    return this.loginWithRedirect({
      tenantId,
      organizationId,
    });
  }

  isAccessTokenExpired(bufferSeconds = 0): boolean {
    const expiresAt = this.state.tokens.expiresAt ?? readTokenExpiration(this.state.tokens.accessToken);
    if (!expiresAt) {
      return true;
    }

    return expiresAt - bufferSeconds <= this.epochSeconds();
  }

  private isRefreshTokenExpired(bufferSeconds = 0): boolean {
    const refreshTokenExpiry = readTokenExpiration(this.state.tokens.refreshToken);
    if (!refreshTokenExpiry) {
      return true;
    }

    return refreshTokenExpiry - bufferSeconds <= this.epochSeconds();
  }

  private epochSeconds(): number {
    return Math.floor(Date.now() / 1000);
  }

  private usesMemoryTokenStorage(): boolean {
    return this.config.tokenStorage === "memory";
  }

  private setState(state: AuthState): void {
    this.state = state;
    this.persistToStorage(state);
    this.subscribers.forEach((subscriber) => subscriber(state));
  }
}
